import { useEffect, useState } from "react";
import { Card, message, Modal, Tooltip } from "antd";
import Meta from "antd/es/card/Meta";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { getLLMUpdatedSelectedFeature, safeParse } from "@/utils/alphaAgent/getRenderAgentData";

// ImageButton Component
const ImageButton = ({ title, subtitle, image, selected, onClick }) => {
  const [isSelected, setIsSelected] = useState(selected);
  useEffect(() => {
    setIsSelected(selected);
  }, [selected]);

  const style = {
    width: 300,
    marginTop: "20px",
    marginBottom: "20px",
    borderRadius: "8px",
  };

  if (isSelected) {
    Object.assign(style, {
      border: "2px solid #ff5c35",
    });
  } else {
    Object.assign(style, {
      border: "2px solid #808080",
    });
  }
  const truncatedSubtitle = subtitle.length > 100 ? subtitle.slice(0, 100).trim() + "..." : subtitle;

  return (
    <Card style={style} cover={<img alt="example" src={image} />} onClick={onClick} hoverable={true}>
      <Meta
        title={title}
        description={
          <Tooltip title={subtitle}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flexWrap: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                height: "4.5em",
                lineHeight: "1.5em",
                maxHeight: "4.5em",
                whiteSpace: "normal",
                wordBreak: "break-word",
                cursor: "pointer",
              }}
            >
              {truncatedSubtitle}
            </div>
          </Tooltip>
        }
      />
    </Card>
  );
};

// CustomFeatures Component
const CustomFeatures = () => {
  const [llmGlobalStateData, setLlmGlobalStateData] = useState<any>({});
  const rawData = useAlphaAgentStore((state) => state.documentData);
  const { data: originalFeatureState } = getLLMUpdatedSelectedFeature(
    rawData?.documentContent,
    rawData?.llmGlobalState
  );
  const {
    updateAnswerByStep,
    addFeature,
    updateFeature,
    deleteFeature,
    removeFeature,
    setCompleteData,
    setData,
    setPopup,
    removeFeatureByInstanceId,
    isInstanceIdInDelete,
    isInstanceIdInUpdate,
    addFeatureToAdd,
    removeFeatureFromAdd,
  } = useAlphaAgentStore.getState();
  useEffect(() => {
    setCompleteData(originalFeatureState);
    const llm = safeParse(rawData?.llmGlobalState);
    setLlmGlobalStateData(llm);
    updateAnswerByStep(1, 9);
  }, []);
  const data = useAlphaAgentStore((state) => state.data);
  const popup = useAlphaAgentStore((state) => state.popup);

  const deleteFeatureHandle = (featureId: string, falseStateName: string, trueStateName: string) => {
    removeFeature(featureId);
    if (originalFeatureState[falseStateName] !== true) {
      if (trueStateName === "percentageChangeChart") {
        deleteFeature(featureId);
      } else if (trueStateName === "addCustomBuiltIndices") {
        for (const customIndex of llmGlobalStateData.customBuiltIndicesForm) {
          deleteFeature(featureId, customIndex.id);
        }
      } else if (trueStateName === "addConstantLineSelection") {
        for (const constantLine of llmGlobalStateData.constantValueLineForm) {
          deleteFeature(featureId, constantLine.id);
        }
      } else if (trueStateName === "addsummaryTable") {
        const summaryTableData = llmGlobalStateData.summaryTableForm;
        if (summaryTableData.companyIndices && summaryTableData.companyIndices.length > 0) {
          for (const companyIndic of summaryTableData.companyIndices) {
            deleteFeature(featureId, companyIndic.id);
          }
        }
        if (summaryTableData.customBuiltIndexes && summaryTableData.customBuiltIndexes.length > 0) {
          for (const customBuiltIndex of summaryTableData.customBuiltIndexes) {
            deleteFeature(featureId, customBuiltIndex.id);
          }
        }
        if (summaryTableData.periods && summaryTableData.periods.length > 0) {
          for (const period of summaryTableData.periods) {
            deleteFeature(featureId, period.id);
          }
        }
      } else if (trueStateName === "addSmallLineChart") {
        const addSmallLineChartData = llmGlobalStateData.smallLineChartForm;
        if (addSmallLineChartData.companyIndices && addSmallLineChartData.companyIndices.length > 0) {
          for (const companyIndic of addSmallLineChartData.companyIndices) {
            deleteFeature(featureId, companyIndic.id);
          }
        }
        if (addSmallLineChartData.customBuiltIndexes && addSmallLineChartData.customBuiltIndexes.length > 0) {
          for (const customBuiltIndex of addSmallLineChartData.customBuiltIndexes) {
            deleteFeature(featureId, customBuiltIndex.id);
          }
        }
        if (addSmallLineChartData.constantValueLines && addSmallLineChartData.constantValueLines.length > 0) {
          for (const constantValueLine of addSmallLineChartData.constantValueLines) {
            deleteFeature(featureId, constantValueLine.id);
          }
        }
      } else if (trueStateName === "addCallOutBoxes") {
        for (const callOutBoxe of llmGlobalStateData.callOutBoxesForm) {
          deleteFeature(featureId, callOutBoxe.id);
        }
      } else if (trueStateName === "addBackgroundShading") {
        for (const backgroundShading of llmGlobalStateData.backgroundShadingForm) {
          deleteFeature(featureId, backgroundShading.id);
        }
      } else if (trueStateName === "addVerticalLine") {
        for (const verticalLine of llmGlobalStateData.verticalLineForm) {
          deleteFeature(featureId, verticalLine.id);
        }
      }
    }

    setData({ name: falseStateName, value: true }, { name: trueStateName, value: false });
  };

  const addFeatureHandle = (featureId: string, falseStateName: string, trueStateName: string) => {
    removeFeature(featureId);
    if (originalFeatureState[trueStateName] !== true) {
      addFeature(featureId);
    }

    setData({ name: falseStateName, value: false }, { name: trueStateName, value: true });
  };

  const handleRadioChange = (featureId: string, instanceId: string, type: string) => {
    removeFeatureByInstanceId(featureId, instanceId);
    if (type === "Update") {
      updateFeature(featureId, instanceId);
    }
    if (type === "Delete") {
      deleteFeature(featureId, instanceId);
    }
  };

  const handleRadioChangeQuestion = (featureId: string, formName: string, checked: "yes" | "no") => {
    if (checked === "yes") {
      if (formName === "") {
        removeFeature(featureId);
        updateFeature(featureId);
      } else {
        setPopup(formName);
      }
    } else {
      removeFeature(featureId);
    }
  };

  const handleAddNew = (id: string) => {
    // Check limits from the actual data in llmGlobalStateData
    let currentCount = 0;
    let limit = 0;

    switch (id) {
      case "constant-value-line":
        currentCount = llmGlobalStateData.constantValueLineForm?.length || 0;
        limit = 5;
        break;
      case "custom-built-indices":
        currentCount = llmGlobalStateData.customBuiltIndicesForm?.length || 0;
        limit = 5;
        break;
      case "call-out-boxes":
        currentCount = llmGlobalStateData.callOutBoxesForm?.length || 0;
        limit = 5;
        break;
      case "background-shading":
        currentCount = llmGlobalStateData.backgroundShadingForm?.length || 0;
        limit = 3;
        break;
      case "vertical-line":
        currentCount = llmGlobalStateData.verticalLineForm?.length || 0;
        limit = 3;
        break;
      default:
        currentCount = 0;
        limit = 999; // No limit for other features
    }

    if (currentCount < limit) {
      addFeatureToAdd(id);
    }
  };

  const handleSubmit = () => {
    // Close the modal and keep all changes
    setPopup("");
    message.success("Changes saved successfully!");
  };

  const handleCancel = () => {
    // Map popup form names to their corresponding feature IDs
    const popupToFeatureIdMap: Record<string, string> = {
      customBuiltIndicesForm: "custom-built-indices",
      constantValueLineForm: "constant-value-line",
      summaryTableForm: "summary-table",
      smallLineChartForm: "small-line-chart",
      callOutBoxesForm: "call-out-boxes",
      backgroundShadingForm: "background-shading",
      verticalLineForm: "vertical-line",
    };

    // Get the current popup and corresponding feature ID
    const currentPopup = useAlphaAgentStore.getState().popup;
    const featureId = popupToFeatureIdMap[currentPopup];

    // Apply the same logic as the "No" radio button
    if (featureId) {
      removeFeature(featureId);
    }

    // Clear any features that were marked to add but not yet committed
    const { featuresData } = useAlphaAgentStore.getState();
    featuresData.featuresToAdd.forEach((featureId) => {
      removeFeatureFromAdd(featureId);
    });

    setPopup("");
    message.info("Changes cancelled");
  };

  const isAddNewButtonDisabled = (id: string) => {
    // Check limits from the actual data in llmGlobalStateData
    let currentCount = 0;

    switch (id) {
      case "constant-value-line":
        currentCount = llmGlobalStateData.constantValueLineForm?.length || 0;
        return currentCount >= 5;
      case "custom-built-indices":
        currentCount = llmGlobalStateData.customBuiltIndicesForm?.length || 0;
        return currentCount >= 5;
      case "call-out-boxes":
        currentCount = llmGlobalStateData.callOutBoxesForm?.length || 0;
        return currentCount >= 5;
      case "background-shading":
        currentCount = llmGlobalStateData.backgroundShadingForm?.length || 0;
        return currentCount >= 3;
      case "vertical-line":
        currentCount = llmGlobalStateData.verticalLineForm?.length || 0;
        return currentCount >= 3;
      default:
        return false;
    }
  };

  const isFeatureAlreadyAdded = (id: string) => {
    const { featuresData } = useAlphaAgentStore.getState();
    return featuresData.featuresToAdd.includes(id);
  };

  const getButtonText = (id: string) => {
    if (isFeatureAlreadyAdded(id)) {
      return "Added for Next Step";
    }
    return "Add New";
  };

  return (
    <div className="px-4 py-10 space-y-8">
      <Modal
        title={
          <h1 className={"text-xl"}>
            <strong>
              {(popup === "customBuiltIndicesForm" && "Custom Built Indices") ||
                (popup === "constantValueLineForm" && "Constant Value Line") ||
                (popup === "summaryTableForm" && "Summary Table") ||
                (popup === "smallLineChartForm" && "Small Line Chart") ||
                (popup === "backgroundShadingForm" && "Background Shading") ||
                (popup === "callOutBoxesForm" && "Call Out Boxes") ||
                (popup === "verticalLineForm" && "Vertical Line")}
            </strong>
          </h1>
        }
        visible={popup !== ""}
        onCancel={() => {
          setPopup("");
        }}
        footer={null}
      >
        {popup === "customBuiltIndicesForm" && (
          <>
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead>
                <tr className="border-b bg-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                </tr>
              </thead>
              <tbody>
                {llmGlobalStateData.customBuiltIndicesForm.map((index) => (
                  <tr key={index.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-800">{index.indexName}</td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.indexName}
                        checked={isInstanceIdInUpdate("custom-built-indices", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("custom-built-indices", index.id, "Update")}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.indexName}
                        checked={isInstanceIdInDelete("custom-built-indices", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("custom-built-indices", index.id, "Delete")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* All Buttons on Same Line - Add New on Left, Cancel/Submit on Right */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => handleAddNew("custom-built-indices")}
                disabled={
                  isAddNewButtonDisabled("custom-built-indices") || isFeatureAlreadyAdded("custom-built-indices")
                }
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer ${
                  isAddNewButtonDisabled("custom-built-indices") || isFeatureAlreadyAdded("custom-built-indices")
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-[#ff5c35] text-white hover:bg-[#e54d2e]"
                }`}
              >
                {getButtonText("custom-built-indices")}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-gray-500 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-[#1f7d3d] text-white hover:bg-[#188f5e]"
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}
        {popup === "constantValueLineForm" && (
          <>
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead>
                <tr className="border-b bg-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                </tr>
              </thead>
              <tbody>
                {llmGlobalStateData.constantValueLineForm.map((index) => (
                  <tr key={index.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-800">{index.lineName}</td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.lineName}
                        checked={isInstanceIdInUpdate("constant-value-line", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("constant-value-line", index.id, "Update")}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.lineName}
                        checked={isInstanceIdInDelete("constant-value-line", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("constant-value-line", index.id, "Delete")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* All Buttons on Same Line - Add New on Left, Cancel/Submit on Right */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => handleAddNew("constant-value-line")}
                disabled={isAddNewButtonDisabled("constant-value-line") || isFeatureAlreadyAdded("constant-value-line")}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer ${
                  isAddNewButtonDisabled("constant-value-line") || isFeatureAlreadyAdded("constant-value-line")
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-[#ff5c35] text-white hover:bg-[#e54d2e]"
                }`}
              >
                {getButtonText("constant-value-line")}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-gray-500 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-[#1f7d3d] text-white hover:bg-[#188f5e]"
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}
        {popup === "summaryTableForm" && (
          <>
            {llmGlobalStateData.summaryTableForm.companyIndices &&
              llmGlobalStateData.summaryTableForm.companyIndices.length > 0 && (
                <>
                  <h2 className={"pt-4 pb-2"}>Companies / Indices:</h2>
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                    <thead>
                      <tr className="border-b bg-gray-200">
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {llmGlobalStateData.summaryTableForm.companyIndices.map((index) => (
                        <tr key={index.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-800">{index.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.name}
                              checked={isInstanceIdInUpdate("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Update")}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.name}
                              checked={isInstanceIdInDelete("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Delete")}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

            {llmGlobalStateData.summaryTableForm.customBuiltIndexes &&
              llmGlobalStateData.summaryTableForm.customBuiltIndexes.length > 0 && (
                <>
                  <h2 className={"pt-4 pb-2"}>Custom Built Indices:</h2>
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                    <thead>
                      <tr className="border-b bg-gray-200">
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Index Name</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {llmGlobalStateData.summaryTableForm.customBuiltIndexes.map((index) => (
                        <tr key={index.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-800">{index.indexName}</td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.indexName}
                              checked={isInstanceIdInUpdate("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Update")}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.name}
                              checked={isInstanceIdInDelete("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Delete")}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

            {llmGlobalStateData.summaryTableForm.periods && llmGlobalStateData.summaryTableForm.periods.length > 0 && (
              <>
                <h2 className={"pt-4 pb-2"}>Time Periods:</h2>
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                  <thead>
                    <tr className="border-b bg-gray-200">
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Index Name</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {llmGlobalStateData.summaryTableForm.periods.map((index) => (
                      <tr key={index.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-800">{index.periodName}</td>
                        <td className="px-6 py-4 text-sm text-gray-800">
                          <input
                            type="radio"
                            name={index.periodName}
                            checked={isInstanceIdInUpdate("summary-table", index.id)}
                            className="h-5 w-5 accent-[#ff5c35] rounded"
                            onChange={() => handleRadioChange("summary-table", index.id, "Update")}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-800">
                          <input
                            type="radio"
                            name={index.periodName}
                            checked={isInstanceIdInDelete("summary-table", index.id)}
                            className="h-5 w-5 accent-[#ff5c35] rounded"
                            onChange={() => handleRadioChange("summary-table", index.id, "Delete")}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {/* All Buttons on Same Line - Add New on Left, Cancel/Submit on Right */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => handleAddNew("summary-table")}
                disabled={isAddNewButtonDisabled("summary-table") || isFeatureAlreadyAdded("summary-table")}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer ${
                  isAddNewButtonDisabled("summary-table") || isFeatureAlreadyAdded("summary-table")
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-[#ff5c35] text-white hover:bg-[#e54d2e]"
                }`}
              >
                {getButtonText("summary-table")}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-gray-500 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-[#1f7d3d] text-white hover:bg-[#188f5e]"
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}
        {popup === "smallLineChartForm" && (
          <>
            {llmGlobalStateData.smallLineChartForm.companyIndices &&
              llmGlobalStateData.smallLineChartForm.companyIndices.length > 0 && (
                <>
                  <h2 className={"pt-4 pb-2"}>Companies / Indices:</h2>
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                    <thead>
                      <tr className="border-b bg-gray-200">
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {llmGlobalStateData.smallLineChartForm.companyIndices.map((index) => (
                        <tr key={index.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-800">{index.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.name}
                              checked={isInstanceIdInUpdate("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Update")}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.name}
                              checked={isInstanceIdInDelete("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Delete")}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

            {llmGlobalStateData.smallLineChartForm.customBuiltIndexes &&
              llmGlobalStateData.smallLineChartForm.customBuiltIndexes.length > 0 && (
                <>
                  <h2 className={"pt-4 pb-2"}>Custom Built Indices:</h2>
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                    <thead>
                      <tr className="border-b bg-gray-200">
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Index Name</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {llmGlobalStateData.smallLineChartForm.customBuiltIndexes.map((index) => (
                        <tr key={index.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-800">{index.indexName}</td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.indexName}
                              checked={isInstanceIdInUpdate("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Update")}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.indexName}
                              checked={isInstanceIdInDelete("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Delete")}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

            {llmGlobalStateData.smallLineChartForm.constantValueLines &&
              llmGlobalStateData.smallLineChartForm.constantValueLines.length > 0 && (
                <>
                  <h2 className={"pt-4 pb-2"}>Constant Value Line:</h2>
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                    <thead>
                      <tr className="border-b bg-gray-200">
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Line Name</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {llmGlobalStateData.smallLineChartForm.constantValueLines.map((index) => (
                        <tr key={index.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-800">{index.lineName}</td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.lineName}
                              checked={isInstanceIdInUpdate("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Update")}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800">
                            <input
                              type="radio"
                              name={index.lineName}
                              checked={isInstanceIdInDelete("summary-table", index.id)}
                              className="h-5 w-5 accent-[#ff5c35] rounded"
                              onChange={() => handleRadioChange("summary-table", index.id, "Delete")}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            {/* All Buttons on Same Line - Add New on Left, Cancel/Submit on Right */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => handleAddNew("small-line-chart")}
                disabled={isAddNewButtonDisabled("small-line-chart") || isFeatureAlreadyAdded("small-line-chart")}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer ${
                  isAddNewButtonDisabled("small-line-chart") || isFeatureAlreadyAdded("small-line-chart")
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-[#ff5c35] text-white hover:bg-[#e54d2e]"
                }`}
              >
                {getButtonText("small-line-chart")}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-gray-500 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-[#1f7d3d] text-white hover:bg-[#188f5e]"
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}
        {popup === "callOutBoxesForm" && (
          <>
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead>
                <tr className="border-b bg-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                </tr>
              </thead>
              <tbody>
                {llmGlobalStateData.callOutBoxesForm.map((index) => (
                  <tr key={index.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-800">{index.company.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.id}
                        checked={isInstanceIdInUpdate("call-out-boxes", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("call-out-boxes", index.id, "Update")}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.id}
                        checked={isInstanceIdInDelete("call-out-boxes", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("call-out-boxes", index.id, "Delete")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* All Buttons on Same Line - Add New on Left, Cancel/Submit on Right */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => handleAddNew("call-out-boxes")}
                disabled={isAddNewButtonDisabled("call-out-boxes") || isFeatureAlreadyAdded("call-out-boxes")}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer ${
                  isAddNewButtonDisabled("call-out-boxes") || isFeatureAlreadyAdded("call-out-boxes")
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-[#ff5c35] text-white hover:bg-[#e54d2e]"
                }`}
              >
                {getButtonText("call-out-boxes")}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-gray-500 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-[#1f7d3d] text-white hover:bg-[#188f5e]"
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}
        {popup === "backgroundShadingForm" && (
          <>
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead>
                <tr className="border-b bg-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                </tr>
              </thead>
              <tbody>
                {llmGlobalStateData.backgroundShadingForm.map((index) => (
                  <tr key={index.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-800">{index.label}</td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.id}
                        checked={isInstanceIdInUpdate("background-shading", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("background-shading", index.id, "Update")}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.id}
                        checked={isInstanceIdInDelete("background-shading", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("background-shading", index.id, "Delete")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* All Buttons on Same Line - Add New on Left, Cancel/Submit on Right */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => handleAddNew("background-shading")}
                disabled={isAddNewButtonDisabled("background-shading") || isFeatureAlreadyAdded("background-shading")}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer ${
                  isAddNewButtonDisabled("background-shading") || isFeatureAlreadyAdded("background-shading")
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-[#ff5c35] text-white hover:bg-[#e54d2e]"
                }`}
              >
                {getButtonText("background-shading")}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-gray-500 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-[#1f7d3d] text-white hover:bg-[#188f5e]"
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}

        {popup === "verticalLineForm" && (
          <>
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead>
                <tr className="border-b bg-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Update</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Delete</th>
                </tr>
              </thead>
              <tbody>
                {llmGlobalStateData.verticalLineForm.map((index) => (
                  <tr key={index.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-800">{index.label}</td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.id}
                        checked={isInstanceIdInUpdate("vertical-line", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("vertical-line", index.id, "Update")}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <input
                        type="radio"
                        name={index.id}
                        checked={isInstanceIdInDelete("vertical-line", index.id)}
                        className="h-5 w-5 accent-[#ff5c35] rounded"
                        onChange={() => handleRadioChange("vertical-line", index.id, "Delete")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* All Buttons on Same Line - Add New on Left, Cancel/Submit on Right */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => handleAddNew("vertical-line")}
                disabled={isAddNewButtonDisabled("vertical-line") || isFeatureAlreadyAdded("vertical-line")}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer ${
                  isAddNewButtonDisabled("vertical-line") || isFeatureAlreadyAdded("vertical-line")
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-[#ff5c35] text-white hover:bg-[#e54d2e]"
                }`}
              >
                {getButtonText("vertical-line")}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-gray-500 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 rounded-md font-medium transition-colors duration-200 cursor-pointer bg-[#1f7d3d] text-white hover:bg-[#188f5e]"
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Text Section: Indexation Adjustment Question */}
      <div>
        <p className="mb-2">
          <strong>
            Having company share prices displayed by their actual traded value on a chart may hinder comparability as
            they can have vastly different starting points. Select here if you would like your chart to have a single
            starting point with daily implied percentage change values driving the change over time.
          </strong>
        </p>
        {/* Grid: Percentage Change Chart vs Actual Traded Value Chart */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Percentage Change Chart"
            subtitle="Each line on the chart starts at a user-selected value on the primary vertical axis, enhancing line comparability."
            image="/assets/line_chart/Index.png"
            selected={data?.percentageChangeChart}
            onClick={() =>
              addFeatureHandle("percentage-change-chart", "actualTradedValueChart", "percentageChangeChart")
            }
          />
          <ImageButton
            title="Actual Traded Value Chart"
            subtitle="No indexation adjustment is made"
            image="/assets/line_chart/noIndex.png"
            selected={data?.actualTradedValueChart}
            onClick={() =>
              deleteFeatureHandle("percentage-change-chart", "actualTradedValueChart", "percentageChangeChart")
            }
          />
        </div>

        {/* Checkbox to trigger popup */}
        {data?.percentageChangeChart && originalFeatureState.percentageChangeChart && (
          <div className="flex items-center gap-2 border border-gray-300 shadow-lg p-4 rounded-lg">
            {/* Question */}
            <p className="text-sm ml-2">
              <strong>
                Do you need to edit the Feature Details previously selected for this Custom Feature (will be done in the
                next step)?
              </strong>
            </p>
            {/* Radio Button for Yes */}
            <input
              id="editFeatureDetailsValueChartYes"
              type="radio"
              name="editFeatureDetailsValueChart"
              onChange={() => handleRadioChangeQuestion("percentage-change-chart", "", "yes")}
              className="h-4 w-4 accent-[#ff5c35]"
            />
            <label htmlFor="editFeatureDetailsValueChartYes" className="text-sm">
              <strong>Yes</strong>
            </label>

            {/* Radio Button for No */}
            <input
              id="editFeatureDetailsValueChartNo"
              type="radio"
              name="editFeatureDetailsValueChart"
              onChange={() => handleRadioChangeQuestion("percentage-change-chart", "", "no")}
              className="h-4 w-4 accent-[#ff5c35]"
              defaultChecked
            />
            <label htmlFor="editFeatureDetailsValueChartNo" className="text-sm">
              <strong>No</strong>
            </label>
          </div>
        )}
      </div>
      {/* Text Section: Include Custom Built Index Question */}
      <div>
        <p className="mb-2">
          <strong>
            Would you like to add constant value line(s) to your chart? These line(s) represent fixed values such as an
            offer price or median trading value and are displayed as dashed lines on the chart.
          </strong>
        </p>
        {/* Grid: Custom Built Indices */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Add Custom Built Indices"
            subtitle="Allows you to add a custom built index made up of select companies as chosen by you."
            image="/assets/line_chart/customBuildIndexLine.png"
            selected={data?.addCustomBuiltIndices}
            onClick={() => addFeatureHandle("custom-built-indices", "noCustomBuiltIndices", "addCustomBuiltIndices")}
          />
          <ImageButton
            title="No Custom Built Indices"
            subtitle="No custom built index lines are added to your chart."
            image="/assets/line_chart/noCustomBuildIndexLine.png"
            selected={data?.noCustomBuiltIndices}
            onClick={() => deleteFeatureHandle("custom-built-indices", "noCustomBuiltIndices", "addCustomBuiltIndices")}
          />
        </div>
        {data?.addCustomBuiltIndices && originalFeatureState.addCustomBuiltIndices && (
          <div className="flex items-center gap-2 border border-gray-300 shadow-lg p-4 rounded-lg">
            {/* Question */}
            <p className="text-sm ml-2">
              <strong>
                Do you need to edit the Feature Details previously selected for this Custom Feature (will be done in the
                next step)?
              </strong>
            </p>
            {/* Radio Button for Yes */}
            <input
              id="editAddCustomBuiltIndicesYes"
              type="radio"
              name="editAddCustomBuiltIndices"
              onChange={() => handleRadioChangeQuestion("custom-built-indices", "customBuiltIndicesForm", "yes")}
              className="h-4 w-4 accent-[#ff5c35]"
            />
            <label htmlFor="editAddCustomBuiltIndicesYes" className="text-sm">
              <strong>Yes</strong>
            </label>

            {/* Radio Button for No */}
            <input
              id="editAddCustomBuiltIndicesNo"
              type="radio"
              name="editAddCustomBuiltIndices"
              onChange={() => handleRadioChangeQuestion("custom-built-indices", "customBuiltIndicesForm", "no")}
              className="h-4 w-4 accent-[#ff5c35]"
              defaultChecked
            />
            <label htmlFor="editAddCustomBuiltIndicesNo" className="text-sm">
              <strong>No</strong>
            </label>
          </div>
        )}
      </div>
      {/* Text Section: Include Static Line Question */}
      <div>
        <p className="mb-4">
          <strong>
            Would you like to add constant value line(s) to your chart? These line(s) represent fixed values such as an
            offer price or median trading value and are displayed as dashed lines on the chart.
          </strong>
        </p>
        {/* Grid: Constant Line Selection */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Add Constant Value Line(s)"
            subtitle="Add a horizontal line at a specified value on the Primary Vertical Axis. This helps track specific metrics like median or target prices."
            image="/assets/line_chart/staticValueLine.png"
            selected={data?.addConstantLineSelection}
            onClick={() =>
              addFeatureHandle("constant-value-line", "noConstantLineSelection", "addConstantLineSelection")
            }
          />
          <ImageButton
            title="No Constant Value Line(s)"
            subtitle="Choose this if you prefer not to add any fixed lines to your chart."
            image="/assets/line_chart/NostaticLine.png"
            selected={data?.noConstantLineSelection}
            onClick={() =>
              deleteFeatureHandle("constant-value-line", "noConstantLineSelection", "addConstantLineSelection")
            }
          />
        </div>
        {data?.addConstantLineSelection && originalFeatureState.addConstantLineSelection && (
          <div className="flex items-center gap-2 border border-gray-300 shadow-lg p-4 rounded-lg border border-gray-300 shadow-lg p-4 rounded-lg">
            {/* Question */}
            <p className="text-sm ml-2">
              <strong>
                Do you need to edit the Feature Details previously selected for this Custom Feature (will be done in the
                next step)?
              </strong>
            </p>
            {/* Radio Button for Yes */}
            <input
              id="editAddConstantLineSelectionYes"
              type="radio"
              name="editAddConstantLineSelection"
              onChange={() => handleRadioChangeQuestion("constant-value-line", "constantValueLineForm", "yes")}
              className="h-4 w-4 accent-[#ff5c35]"
            />
            <label htmlFor="editAddConstantLineSelectionYes" className="text-sm">
              <strong>Yes</strong>
            </label>

            {/* Radio Button for No */}
            <input
              id="editAddConstantLineSelectionNo"
              type="radio"
              name="editAddConstantLineSelection"
              onChange={() => handleRadioChangeQuestion("constant-value-line", "constantValueLineForm", "no")}
              className="h-4 w-4 accent-[#ff5c35]"
              defaultChecked
            />
            <label htmlFor="editAddConstantLineSelectionNo" className="text-sm">
              <strong>No</strong>
            </label>
          </div>
        )}
      </div>
      {/* Trading Volume Question */}
      <div>
        <p className="mb-2">
          <strong>
            Do you want to display the trading volume of the primary company on the chart? We can include this on the
            secondary axis formatted as a column chart. It can be helpful to emphasize particular changes in trading
            pattern for the primary company.
          </strong>
        </p>
        {/* Grid: Trading Volume */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Add Trading Volume"
            subtitle="Adds trading volume over time as a column chart to the secondary vertical axis."
            image="/assets/line_chart/Q1_AddSecondaryVerticalAxisBarChart.png"
            selected={data?.addsecondaryVerticalAxisColumnChart}
            onClick={() =>
              setData(
                { name: "addsecondaryVerticalAxisColumnChart", value: true },
                { name: "nosecondaryVerticalAxisColumnChart", value: false }
              )
            }
          />
          <ImageButton
            title="No Trading Volume"
            subtitle="No trading volume data to your chart."
            image="/assets/line_chart/Q1_NoIndexation.png"
            selected={data?.nosecondaryVerticalAxisColumnChart}
            onClick={() =>
              setData(
                { name: "addsecondaryVerticalAxisColumnChart", value: false },
                { name: "nosecondaryVerticalAxisColumnChart", value: true }
              )
            }
          />
        </div>
      </div>
      {/* Summary Table Question */}
      <div>
        <p className="mb-4">
          <strong>
            Do you want to include a trading performance summary table on the chart? We can include this on an
            appropriate top corner of your chart. It can be helpful to emphasize periodic changes in share price
            performance.
          </strong>
        </p>
        {/* Grid: Summary Table */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Add Summary Table"
            subtitle="Adds a summary table on select company / index trading performance at various points in time."
            image="/assets/line_chart/Q2_AddSummaryTable.png"
            selected={data?.addsummaryTable}
            onClick={() => {
              if (!data?.nosmallLineChart) {
                message.error(
                  "There is not enough space on the chart to include both the Summary Table and the Small " +
                    "Line Chart features on your primary chart. Please select only one of these options."
                );
              } else if (!data?.noCallOutBoxes) {
                message.error(
                  "You cannot select both the Summary Table and Call Out Boxes features on the same chart. " +
                    "Please select only one of these options."
                );
              } else {
                addFeatureHandle("summary-table", "nosummaryTable", "addsummaryTable");
              }
            }}
          />
          <ImageButton
            title="No Summary Table"
            subtitle="No Summary Table added to your chart."
            image="/assets/line_chart/Q2_NoSummaryTable.png"
            selected={data?.nosummaryTable}
            onClick={() => deleteFeatureHandle("summary-table", "nosummaryTable", "addsummaryTable")}
          />
        </div>
        {data?.addsummaryTable && originalFeatureState.addsummaryTable && (
          <div className="flex items-center gap-2 border border-gray-300 shadow-lg p-4 rounded-lg">
            {/* Question */}
            <p className="text-sm ml-2">
              <strong>
                Do you need to edit the Feature Details previously selected for this Custom Feature (will be done in the
                next step)?
              </strong>
            </p>
            {/* Radio Button for Yes */}
            <input
              id="editAddsummaryTableYes"
              type="radio"
              name="editAddsummaryTable"
              onChange={() => handleRadioChangeQuestion("summary-table", "summaryTableForm", "yes")}
              className="h-4 w-4 accent-[#ff5c35]"
            />
            <label htmlFor="editAddsummaryTableYes" className="text-sm">
              <strong>Yes</strong>
            </label>

            {/* Radio Button for No */}
            <input
              id="editAddsummaryTableNo"
              type="radio"
              name="editAddsummaryTable"
              onChange={() => handleRadioChangeQuestion("summary-table", "summaryTableForm", "no")}
              className="h-4 w-4 accent-[#ff5c35]"
              defaultChecked
            />
            <label htmlFor="editAddsummaryTableNo" className="text-sm">
              <strong>No</strong>
            </label>
          </div>
        )}
      </div>
      {/* Question 3: Small Line Chart */}
      <div>
        <p className="mb-2">
          <strong>
            Do you want to include a small line chart over a selected time period to your placeholder? It can be helpful
            to emphasize a key time period in the primary chart's trading period. And can be added to the appropriate
            top corner of your chart.
          </strong>
        </p>
        {/* Grid: Small Line Chart */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Add Small Line Chart"
            subtitle="Adds a small line chart on select company / index trading performance over a specified period of time."
            image="/assets/line_chart/Q3_AddSmallLineChart.png"
            selected={data?.addSmallLineChart}
            onClick={() => {
              if (data?.nosummaryTable) {
                addFeatureHandle("small-line-chart", "nosmallLineChart", "addSmallLineChart");
              } else {
                message.error(
                  "There is not enough space on the chart to include both the Summary Table and the Small " +
                    "Line Chart features on your primary chart. Please select only one of these options."
                );
              }
            }}
          />
          <ImageButton
            title="No Small Line Chart"
            subtitle="No Small Line Chart added to your chart."
            image="/assets/line_chart/Q3_NoSmallLineChart.png"
            selected={data?.nosmallLineChart}
            onClick={() => deleteFeatureHandle("small-line-chart", "nosmallLineChart", "addSmallLineChart")}
          />
        </div>
        {data?.addSmallLineChart && originalFeatureState.addSmallLineChart && (
          <div className="flex items-center gap-2 border border-gray-300 shadow-lg p-4 rounded-lg">
            {/* Question */}
            <p className="text-sm ml-2">
              <strong>
                Do you need to edit the Feature Details previously selected for this Custom Feature (will be done in the
                next step)?
              </strong>
            </p>
            {/* Radio Button for Yes */}
            <input
              id="editAddSmallLineChartYes"
              type="radio"
              name="editAddSmallLineChart"
              onChange={() => handleRadioChangeQuestion("small-line-chart", "smallLineChartForm", "yes")}
              className="h-4 w-4 accent-[#ff5c35]"
            />
            <label htmlFor="editAddSmallLineChartYes" className="text-sm">
              <strong>Yes</strong>
            </label>

            {/* Radio Button for No */}
            <input
              id="editAddSmallLineChartNo"
              type="radio"
              name="editAddSmallLineChart"
              onChange={() => handleRadioChangeQuestion("small-line-chart", "smallLineChartForm", "no")}
              className="h-4 w-4 accent-[#ff5c35]"
              defaultChecked
            />
            <label htmlFor="editAddSmallLineChartNo" className="text-sm">
              <strong>No</strong>
            </label>
          </div>
        )}
      </div>
      {/* Question 4: Call Out Boxes */}
      <div>
        <p className="mb-2">
          <strong>
            Do you want to include call-out boxes on the chart? May be helpful to emphasize certain changes in share
            price at particular points in time.
          </strong>
        </p>
        {/* Grid: Call Out Boxes */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Add Call Out Boxes"
            subtitle="Adds specified Call Out Boxes to the Line Chart."
            image="/assets/line_chart/Q4_AddCallOutBoxes.png"
            selected={data?.addCallOutBoxes}
            onClick={() => {
              if (!data?.nosummaryTable) {
                message.error(
                  "You cannot select both the Summary Table and Call Out Boxes features on the same chart. " +
                    "Please select only one of these options."
                );
              } else {
                addFeatureHandle("call-out-boxes", "noCallOutBoxes", "addCallOutBoxes");
              }
            }}
          />
          <ImageButton
            title="No Call Out Boxes"
            subtitle="No Call Out Boxes added to your chart."
            image="/assets/line_chart/Q4_NoCallOutBoxes.png"
            selected={data?.noCallOutBoxes}
            onClick={() => deleteFeatureHandle("call-out-boxes", "noCallOutBoxes", "addCallOutBoxes")}
          />
        </div>
        {data?.addCallOutBoxes && originalFeatureState.addCallOutBoxes && (
          <div className="flex items-center gap-2 border border-gray-300 shadow-lg p-4 rounded-lg">
            {/* Question */}
            <p className="text-sm ml-2">
              <strong>
                Do you need to edit the Feature Details previously selected for this Custom Feature (will be done in the
                next step)?
              </strong>
            </p>
            {/* Radio Button for Yes */}
            <input
              id="editAddCallOutBoxesYes"
              type="radio"
              name="editAddCallOutBoxes"
              onChange={() => handleRadioChangeQuestion("call-out-boxes", "callOutBoxesForm", "yes")}
              className="h-4 w-4 accent-[#ff5c35]"
            />
            <label htmlFor="editAddCallOutBoxesYes" className="text-sm">
              <strong>Yes</strong>
            </label>

            {/* Radio Button for No */}
            <input
              id="editAddCallOutBoxesNo"
              type="radio"
              name="editAddCallOutBoxes"
              onChange={() => handleRadioChangeQuestion("call-out-boxes", "callOutBoxesForm", "no")}
              className="h-4 w-4 accent-[#ff5c35]"
              defaultChecked
            />
            <label htmlFor="editAddCallOutBoxesNo" className="text-sm">
              <strong>No</strong>
            </label>
          </div>
        )}
      </div>
      {/* Question 5: Background Shading */}
      <div>
        <p className="mb-2">
          <strong>
            Do you want to include background shading over chosen trading time periods? It can be helpful to emphasize
            market-wide trading phenomena's impact on particular periods of the chart (such as Covid panic or an AI
            exuberance rally).
          </strong>
        </p>
        {/* Grid: Background Shading */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Add Background Shading"
            subtitle="Adds specified Background Shading to the Line Chart."
            image="/assets/line_chart/Q5_AddBackgroundShading.png"
            selected={data?.addBackgroundShading}
            onClick={() => addFeatureHandle("background-shading", "noBackgroundShading", "addBackgroundShading")}
          />
          <ImageButton
            title="No Background Shading"
            subtitle="No Background Shading added to your chart."
            image="/assets/line_chart/Q5_NoBackgroundShading.png"
            selected={data?.noBackgroundShading}
            onClick={() => deleteFeatureHandle("background-shading", "noBackgroundShading", "addBackgroundShading")}
          />
        </div>
        {data?.addBackgroundShading && originalFeatureState.addBackgroundShading && (
          <div className="flex items-center gap-2 border border-gray-300 shadow-lg p-4 rounded-lg">
            {/* Question */}
            <p className="text-sm ml-2">
              <strong>
                Do you need to edit the Feature Details previously selected for this Custom Feature (will be done in the
                next step)?
              </strong>
            </p>
            {/* Radio Button for Yes */}
            <input
              id="editAddBackgroundShadingYes"
              type="radio"
              name="editAddBackgroundShading"
              onChange={() => handleRadioChangeQuestion("background-shading", "backgroundShadingForm", "yes")}
              className="h-4 w-4 accent-[#ff5c35]"
            />
            <label htmlFor="editAddBackgroundShadingYes" className="text-sm">
              <strong>Yes</strong>
            </label>

            {/* Radio Button for No */}
            <input
              id="editAddBackgroundShadingNo"
              type="radio"
              name="editAddBackgroundShading"
              onChange={() => handleRadioChangeQuestion("background-shading", "backgroundShadingForm", "no")}
              className="h-4 w-4 accent-[#ff5c35]"
              defaultChecked
            />
            <label htmlFor="editAddBackgroundShadingNo" className="text-sm">
              <strong>No</strong>
            </label>
          </div>
        )}
      </div>
      {/* Question 6: Vertical Line */}
      <div>
        <p className="mb-4">
          <strong>
            Do you want to include vertical line(s) onto the chart? Can be helpful to emphasize certain points in time
            where trading behavior changes (such as a big company public announcement).
          </strong>
        </p>
        {/* Grid: Vertical Line */}
        <div className="flex justify-center gap-6">
          <ImageButton
            title="Add Vertical Line"
            subtitle="Add specified Vertical Line at selection Points-in-Time onto the Line Chart."
            image="/assets/line_chart/Q6_AddVerticalLine.png"
            selected={data?.addVerticalLine}
            onClick={() => addFeatureHandle("vertical-line", "noVerticalLine", "addVerticalLine")}
          />
          <ImageButton
            title="No Vertical Line"
            subtitle="No Vertical Line added to your chart."
            image="/assets/line_chart/Q6_NoVerticalLine.png"
            selected={data?.noVerticalLine}
            onClick={() => deleteFeatureHandle("vertical-line", "noVerticalLine", "addVerticalLine")}
          />
        </div>
        {data?.addVerticalLine && originalFeatureState.addVerticalLine && (
          <div className="flex items-center gap-2 border border-gray-300 shadow-lg p-4 rounded-lg">
            {/* Question */}
            <p className="text-sm ml-2">
              <strong>
                Do you need to edit the Feature Details previously selected for this Custom Feature (will be done in the
                next step)?
              </strong>
            </p>
            {/* Radio Button for Yes */}
            <input
              id="editAddVerticalLineYes"
              type="radio"
              name="editAddVerticalLine"
              onChange={() => handleRadioChangeQuestion("vertical-line", "verticalLineForm", "yes")}
              className="h-4 w-4 accent-[#ff5c35]"
            />
            <label htmlFor="editAddVerticalLineYes" className="text-sm">
              <strong>Yes</strong>
            </label>

            {/* Radio Button for No */}
            <input
              id="editAddVerticalLineNo"
              type="radio"
              name="editAddVerticalLine"
              onChange={() => handleRadioChangeQuestion("vertical-line", "verticalLineForm", "no")}
              className="h-4 w-4 accent-[#ff5c35]"
              defaultChecked
            />
            <label htmlFor="editAddVerticalLineNo" className="text-sm">
              <strong>No</strong>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFeatures;
